import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { updateUserProfile, changePassword } from '../utils/apiServices';
import '../stylesheets/UserProfile.css'; 

// Opciones para el tipo de documento
const identificationTypes = [
  { value: 'CC', label: 'Cédula de Ciudadanía' },
  { value: 'CE', label: 'Cédula de Extranjería' },
  { value: 'NIT', label: 'NIT' },
  { value: 'PAS', label: 'Pasaporte' },
  { value: 'OTRO', label: 'Otro' },
];

const UserProfile = () => {
    const { user, profile, loading, updateProfile } = useContext(AuthContext);

    const [profileData, setProfileData] = useState({
        document: '',
        identification_type: '',
        birth_date: '',
        phone_number: '',
    });

    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });

    const [profileMessage, setProfileMessage] = useState({ type: '', content: '' });
    const [passwordMessage, setPasswordMessage] = useState({ type: '', content: '' });
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    useEffect(() => {
        if (profile) {
            setProfileData({
                document: profile.document || '',
                identification_type: profile.identification_type || 'CC',
                birth_date: profile.birth_date || '',
                phone_number: profile.phone_number || '',
            });
        }
    }, [profile]);

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setIsUpdatingProfile(true);
        setProfileMessage({ type: '', content: '' });

        try {
            const updatedProfile = await updateUserProfile(user.token, profileData);
            updateProfile(updatedProfile); 
            setProfileMessage({ type: 'success', content: '¡Perfil actualizado con éxito!' });
        } catch (error) {
            setProfileMessage({ type: 'error', content: 'Error al actualizar. Revisa los campos.' });
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (passwordData.new_password !== passwordData.confirm_password) {
            setPasswordMessage({ type: 'error', content: 'Las nuevas contraseñas no coinciden.' });
            return;
        }

        setIsChangingPassword(true);
        setPasswordMessage({ type: '', content: '' });

        try {
            await changePassword(user.token, passwordData.current_password, passwordData.new_password);
            setPasswordMessage({ type: 'success', content: '¡Contraseña cambiada con éxito!' });
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Error al cambiar la contraseña. Verifica tu contraseña actual.';
            setPasswordMessage({ type: 'error', content: errorMsg });
        } finally {
            setIsChangingPassword(false);
        }
    };

    if (loading) {
        return <div className="profile-page-container profile-loading">Cargando perfil...</div>;
    }

    if (!user || !profile) {
        return <div>No se pudo cargar la información del perfil.</div>;
    }

    return (
        <div className="profile-page-container">
            <h1>Mi Perfil</h1>
            <p className="profile-subtitle">Administra tu información personal y seguridad de tu cuenta</p>
            
            <div className="profile-grid">
                <div className="section-card">
                    <h2>Información Básica</h2>
                    <div className="info-item">
                        <span className="info-label">Nombre:</span>
                        <span className="info-value">{user.full_name}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Usuario:</span>
                        <span className="info-value">{user.username}</span>
                    </div>
                    <div className="info-item">
                        <span className="info-label">Correo:</span>
                        <span className="info-value">{user.email}</span>
                    </div>
                </div>

                <div className="section-card">
                    <h2>Información Personal</h2>
                    <form onSubmit={handleProfileSubmit}>
                        <div className="form-group">
                            <label htmlFor="identification_type">Tipo de Identificación</label>
                            <select 
                                id="identification_type"
                                name="identification_type"
                                value={profileData.identification_type}
                                onChange={handleProfileChange}
                            >
                                {identificationTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="document">Número de Documento</label>
                            <input 
                                type="text"
                                id="document"
                                name="document"
                                value={profileData.document}
                                onChange={handleProfileChange}
                                placeholder="Tu número de documento"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="birth_date">Fecha de Nacimiento</label>
                            <input 
                                type="date"
                                id="birth_date"
                                name="birth_date"
                                value={profileData.birth_date}
                                onChange={handleProfileChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone_number">Número de Teléfono</label>
                            <input 
                                type="tel"
                                id="phone_number"
                                name="phone_number"
                                value={profileData.phone_number}
                                onChange={handleProfileChange}
                                placeholder="Ej: +573001234567"
                            />
                        </div>
                        
                        <div className="form-actions">
                            <button type="submit" disabled={isUpdatingProfile}>
                                {isUpdatingProfile ? 'Actualizando...' : 'Guardar Perfil'}
                            </button>
                        </div>

                        {profileMessage.content && (
                            <div className={`message ${profileMessage.type}`}>{profileMessage.content}</div>
                        )}
                    </form>
                </div>

                <div className="section-card">
                    <h2>Cambiar Contraseña</h2>
                    <form onSubmit={handlePasswordSubmit}>
                        <div className="form-group">
                            <label htmlFor="current_password">Contraseña Actual</label>
                            <input 
                                type="password"
                                id="current_password"
                                name="current_password"
                                value={passwordData.current_password}
                                onChange={handlePasswordChange}
                                required
                                placeholder="Tu contraseña actual"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="new_password">Nueva Contraseña</label>
                            <input 
                                type="password"
                                id="new_password"
                                name="new_password"
                                value={passwordData.new_password}
                                onChange={handlePasswordChange}
                                required
                                placeholder="Mínimo 8 caracteres"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirm_password">Confirmar Nueva Contraseña</label>
                            <input 
                                type="password"
                                id="confirm_password"
                                name="confirm_password"
                                value={passwordData.confirm_password}
                                onChange={handlePasswordChange}
                                required
                                placeholder="Repite la nueva contraseña"
                            />
                        </div>

                        <div className="form-actions">
                            <button type="submit" disabled={isChangingPassword}>
                                {isChangingPassword ? 'Cambiando...' : 'Cambiar Contraseña'}
                            </button>
                        </div>

                        {passwordMessage.content && (
                            <div className={`message ${passwordMessage.type}`}>{passwordMessage.content}</div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserProfile;

